import { Film, Mic, Image as ImageIcon } from "lucide-react";
import { useT } from "@/lib/i18n";

const METERS = [
  { key: "video_sec", label: "Video credits", icon: Film, toUnit: (s) => s / 60, color: "from-purple-500 to-cyan-500" },
  { key: "voice_sec", label: "Voice minutes", icon: Mic, toUnit: (s) => s / 60, color: "from-amber-500 to-pink-500" },
  { key: "images", label: "Images", icon: ImageIcon, toUnit: (n) => n, color: "from-emerald-500 to-cyan-500" },
];

const fmt = (n) => (Number.isInteger(n) ? n.toLocaleString("en-IN") : n.toFixed(1));
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

export function QuotaMeters({ subscription, unlimited, whitelistUntil }) {
  const { t } = useT();
  if (unlimited) {
    return (
      <div data-testid="quota-unlimited" className="glass rounded-2xl p-5 text-sm text-slate-300">
        {t("Complimentary access — quotas are not enforced for this account.")}
        {whitelistUntil && <span data-testid="quota-whitelist-until" className="ml-2 text-xs font-mono text-amber-300">{t("Free usage ends on")} {fmtDate(whitelistUntil)}</span>}
      </div>
    );
  }
  if (!subscription) {
    return <div data-testid="quota-none" className="glass rounded-2xl p-5 text-sm text-slate-400">{t("No active pack. Renders are blocked until a prepaid pack is activated.")}</div>;
  }
  const quota = subscription.quota || {}, used = subscription.used || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {METERS.map((m) => {
        const total = m.toUnit(quota[m.key] || 0), spent = m.toUnit(used[m.key] || 0);
        const left = Math.max(0, total - spent), pct = total ? Math.min(100, (spent / total) * 100) : 0;
        return (
          <div key={m.key} data-testid={`quota-${m.key}`} className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><m.icon size={12} /> {t(m.label)}</span>
              <span className={`text-[10px] font-mono ${pct > 90 ? "text-red-400" : "text-slate-500"}`}>{pct.toFixed(0)}% {t("used")}</span>
            </div>
            <div className="text-2xl font-display font-extrabold font-mono">{fmt(left)} <span className="text-sm text-slate-500 font-sans font-normal">{t("left")}</span></div>
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className={`h-full rounded-full bg-gradient-to-r ${m.color}`} style={{ width: `${pct}%` }} /></div>
            <div className="mt-1.5 text-[10px] font-mono text-slate-500">{fmt(spent)} {t("consumed")} · {fmt(total)} {t("total")}</div>
          </div>
        );
      })}
    </div>
  );
}
